import { useMemo, useCallback } from "react";
import i18n from "i18next";
import { useSelector, useStore } from "react-redux";
import { useStripe, useElements } from "@stripe/react-stripe-js";

import {
  accountPlanSelector,
  accountSubscriptionStatusSelector,
  SUBSCRIPTION_PLANS,
  SUBSCRIPTION_STATUS,
  userPriceOptionSelector,
  userDiscountSelector,
  getTickerUsingId,
  getExchangeRate,
  getOwnershipValueForCustodian,
  calcCustodianOwnershipValue,
  getCustodianAdjustmentInCurrency,
  convertCurrency,
  defaultDashboardChartIdsSelector,
  recapDataPortfolioMapUpdateTimestampSelector,
  isRecapDataAvailableSelector,
  parseParams,
  recapChartOptions,
  chartKeyParams,
  getColumnForChart,
  PVST_VALUE_TICKER_ID,
  userEmailSelector,
  accountPaymentMethodSelector,
  confirmCardSetupErrorCodes,
  isAppInWhiteLabelMode,
  hasEnabledAutopilotRules,
  isFiatManualCustodian,
  getHomeLTV,
  getTickerUsingShortName,
  shortFormatNumberWithCurrency
} from "@kubera/common";

import { CardElement } from "components/account_settings/AccountCardElement";
import { filterDataFromPortfolioDetails } from "components/grid/GridDataModel";
import { category } from "components/dashboard/DashboardComponentExports";

export const useNextSubscriptionPlanTxt = ({ considerDiscount = false } = {}) => {
  const userDiscount = useSelector(userDiscountSelector);
  const accountPlanFromSelector = useSelector(accountPlanSelector);
  const accountPlan =
    accountPlanFromSelector !== SUBSCRIPTION_PLANS.TRIAL ? SUBSCRIPTION_PLANS.YEARLY : SUBSCRIPTION_PLANS.TRIAL;
  const accountSubscriptionStatus = useSelector(accountSubscriptionStatusSelector);
  const userPriceOption = useSelector(userPriceOptionSelector);

  const isSubscriptionActive =
    accountPlan !== SUBSCRIPTION_PLANS.TRIAL &&
    (accountSubscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE ||
      accountSubscriptionStatus === SUBSCRIPTION_STATUS.TRIALING ||
      accountSubscriptionStatus === SUBSCRIPTION_STATUS.PAST_DUE);
  const defaultSubscriptionPrice =
    accountPlan === SUBSCRIPTION_PLANS.MONTHLY ? userPriceOption.monthly : userPriceOption.yearly;
  const isMorY = accountPlan === SUBSCRIPTION_PLANS.MONTHLY ? "M" : "Y";

  const discount = !considerDiscount ? 0 : defaultSubscriptionPrice * (userDiscount / 100);
  return !isSubscriptionActive
    ? i18n.t(`accountSettings.userSubscritionText${isMorY}`).replace(/%s%/, defaultSubscriptionPrice - discount)
    : "";
};

export const useSheetData = ({ portfolio, category }) => {
  const store = useStore();

  const { details: portfolioDetails, currency: portfolioCurrency } = portfolio;

  const { sheets, sections, firstSheet, sheetTotalHashTable, sectionTotalHashTable } = useMemo(() => {
    let firstSheet = null;
    let sheetTotalHashTable = {};
    let sectionTotalHashTable = {};
    let currentSheetId = null;
    let currentSectionId = null;

    let sheetsArr = [];
    let sectionsArr = [];

    const { sheets, sections } = filterDataFromPortfolioDetails(
      portfolioDetails,
      row => {
        const ownership = getOwnershipValueForCustodian(store.getState(), row.id);
        let exchangeRate = getExchangeRate(
          store.getState(),
          getTickerUsingId(store.getState(), row.valueTickerId).shortName,
          portfolioCurrency
        );

        if (row.valueTickerId === PVST_VALUE_TICKER_ID && row.rate) {
          const rateParsed = JSON.parse(row.rate);
          const tickerForPrivateTicker = getTickerUsingId(store.getState(), rateParsed.t);
          exchangeRate = convertCurrency(
            store.getState(),
            rateParsed.p,
            tickerForPrivateTicker.shortName,
            portfolio.currency
          );
        }
        const computedRowValue =
          (calcCustodianOwnershipValue(row.value, ownership) || 0) * exchangeRate -
          getCustodianAdjustmentInCurrency(store.getState(), row, portfolioCurrency);

        if (row.value !== null) {
          sheetTotalHashTable[currentSheetId].total += computedRowValue;
          sheetTotalHashTable[currentSheetId].hasRowWithValue = true;

          sectionTotalHashTable[currentSectionId].total += computedRowValue;
          sectionTotalHashTable[currentSectionId].hasRowWithValue = true;
        }

        sectionTotalHashTable[currentSectionId].rows[row.id] = {
          ...row,
          ownership,
          hasAutopilotRulesEnabled: isFiatManualCustodian(store.getState(), row) && hasEnabledAutopilotRules(row)
        };

        if (!firstSheet && computedRowValue > 0) {
          firstSheet = sheetTotalHashTable[currentSheetId];
        }

        return true;
      },
      section => {
        currentSectionId = section.id;
        sectionTotalHashTable[currentSectionId] = {
          total: 0,
          hasRowWithValue: false,
          rows: {},
          ...section
        };
        sectionsArr.push(sectionTotalHashTable[currentSectionId]);
        sheetTotalHashTable[currentSheetId].sections[section.id] = section;

        return true;
      },
      sheet => {
        const isCorrectCategorySheet = sheet.category === category;

        if (!isCorrectCategorySheet) {
          return false;
        }

        currentSheetId = sheet.id;
        sheetTotalHashTable[currentSheetId] = {
          total: 0,
          hasRowWithValue: false,
          sections: {},
          ...sheet
        };

        sheetsArr.push(sheetTotalHashTable[currentSheetId]);

        return true;
      }
    );

    sheets.forEach(eachSheet => {
      if (sheetTotalHashTable[eachSheet.id]) {
        sheetTotalHashTable[eachSheet.id].irr = eachSheet.irr;
      }
    });

    sections.forEach(eachSection => {
      if (sectionTotalHashTable[eachSection.id]) {
        sectionTotalHashTable[eachSection.id].irr = eachSection.irr;
      }
    });

    return {
      sheets: sheetsArr,
      sections: sectionsArr,
      firstSheet,
      sheetTotalHashTable,
      sectionTotalHashTable
    };

    //eslint-disable-next-line
  }, [category, portfolioDetails, portfolioCurrency]);

  return {
    sheets,
    sections,
    firstSheet,
    sheetTotalHashTable,
    sectionTotalHashTable
  };
};

const getUniqueChartParamsString = chartParams =>
  `${chartParams.report_id}_${chartParams.chart_style}_${chartParams.chart_content}_${chartParams.chart_name}`;

const getChartComparisonKey = chartParams =>
  `${chartParams.chart_style}_${chartParams.chart_content}_${chartParams.chart_name}`;

const getDefaultChartComparisonKey = chartParams =>
  `${chartParams[chartKeyParams.IS_DEFAULT_CHART]}_${getChartComparisonKey(chartParams)}`;

const createChartParamsParser = () => {
  const cache = new Map();
  return chartId => {
    let chartParams = cache.get(chartId);
    if (chartParams === undefined) {
      chartParams = parseParams(chartId);
      cache.set(chartId, chartParams);
    }
    return chartParams;
  };
};

const getRemainingCharts = (parseChartId, defaultDashboardChartIds, dashboardChartIds) => {
  const dashboardReportNodeIds = new Set();
  const dashboardComparisonKeys = new Set();

  for (const chartId of dashboardChartIds) {
    const dashboardChartParams = parseChartId(chartId);
    dashboardReportNodeIds.add(parseChartId(dashboardChartParams.report_id).report_node_id);
    dashboardComparisonKeys.add(getDefaultChartComparisonKey(dashboardChartParams));
  }

  return defaultDashboardChartIds.filter(chartId => {
    const defaultChartParams = parseChartId(chartId);
    const defaultReportParams = parseChartId(defaultChartParams.report_id);

    if (defaultReportParams.chart_option === recapChartOptions.SHEETS_AND_SECTIONS.id) {
      return !dashboardReportNodeIds.has(defaultReportParams.report_node_id);
    }
    return !dashboardComparisonKeys.has(getDefaultChartComparisonKey(defaultChartParams));
  });
};

export const useDiyChartsData = diyDashboardCharts => {
  const defaultDashboardChartIds = useSelector(defaultDashboardChartIdsSelector) || [];
  const recapDataPortfolioMapUpdateTimestamp = useSelector(recapDataPortfolioMapUpdateTimestampSelector);
  const isRecapDataAvailable = useSelector(isRecapDataAvailableSelector);

  const dashboardChartIds = useMemo(
    () => diyDashboardCharts.columns["column1"].chartIds.concat(diyDashboardCharts.columns["column2"].chartIds),
    [diyDashboardCharts]
  );

  const diyChartsData = useMemo(() => {
    if (!recapDataPortfolioMapUpdateTimestamp)
      return {
        diyDashboardCharts,
        dashboardChartIds
      };

    const parseChartId = createChartParamsParser();
    let diyDashboardChartsTemp = window.kbStructuredClone(diyDashboardCharts);
    let dashboardChartIdsTemp = dashboardChartIds;
    const alreadyPresentChartParamsSet = new Set();
    const defaultColumnsFilterFn = chartId => {
      const dashboardChartParams = parseChartId(chartId);
      const currentChartParamsString = getUniqueChartParamsString(dashboardChartParams);

      const isAlreadyPresent = alreadyPresentChartParamsSet.has(currentChartParamsString);
      if (isAlreadyPresent || dashboardChartParams.chart_name === "undefined") {
        return false;
      }
      alreadyPresentChartParamsSet.add(currentChartParamsString);

      return true;
    };
    let columnsFilterFn = defaultColumnsFilterFn;

    if (dashboardChartIds.length && isRecapDataAvailable) {
      let remainingCharts = getRemainingCharts(parseChartId, defaultDashboardChartIds, dashboardChartIds);
      // An empty default set means the recap-derived defaults could not be computed —
      // defaultDashboardChartIdsSelector also returns [] when a time range it reads is
      // missing — not that the user removed charts. Pruning on it drops their default
      // charts and persists that layout to the server, so it needs a known set.
      const knownDefaultChartIdCount = defaultDashboardChartIds.filter(chart => chart).length;
      if (remainingCharts.length) {
        remainingCharts.forEach((chart, _) => {
          const columnForChart = getColumnForChart(diyDashboardChartsTemp);
          diyDashboardChartsTemp.columns[columnForChart].chartIds.push(chart);
        });
      } else if (
        knownDefaultChartIdCount > 0 &&
        knownDefaultChartIdCount < dashboardChartIds.filter(chart => chart).length
      ) {
        console.log("some charts have been removed");
        // if any of the default chart is removed like user has removed all crypto assets etc
        const defaultReportNodeIds = new Set();
        const defaultComparisonKeys = new Set();
        for (const defaultChartId of defaultDashboardChartIds) {
          if (!defaultChartId) continue;

          const defaultChartParams = parseChartId(defaultChartId);
          const defaultReportParams = parseChartId(defaultChartParams.report_id);
          if (defaultReportParams.chart_option === recapChartOptions.SHEETS_AND_SECTIONS.id) {
            defaultReportNodeIds.add(defaultReportParams.report_node_id);
          } else {
            defaultComparisonKeys.add(getChartComparisonKey(defaultChartParams));
          }
        }

        columnsFilterFn = chartId => {
          const dashboardChartParams = parseChartId(chartId);
          const isDefaultChart = dashboardChartParams[chartKeyParams.IS_DEFAULT_CHART] === "true";

          if (!defaultColumnsFilterFn(chartId)) return false;
          if (!isDefaultChart) return true;

          return (
            defaultReportNodeIds.has(parseChartId(dashboardChartParams.report_id).report_node_id) ||
            defaultComparisonKeys.has(getChartComparisonKey(dashboardChartParams))
          );
        };
      }

      // Prevent duplicates added when computing remaining charts
      const filteredColumn1Charts = diyDashboardChartsTemp.columns.column1.chartIds.filter(columnsFilterFn);
      const filteredColumn2Charts = diyDashboardChartsTemp.columns.column2.chartIds.filter(columnsFilterFn);
      diyDashboardChartsTemp.columns.column1.chartIds = filteredColumn1Charts;
      diyDashboardChartsTemp.columns.column2.chartIds = filteredColumn2Charts;
    }

    return {
      diyDashboardCharts: diyDashboardChartsTemp,
      dashboardChartIds: dashboardChartIdsTemp
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diyDashboardCharts, recapDataPortfolioMapUpdateTimestamp]);

  return diyChartsData;
};

const defaultCardSetupOptions = {
  usePaymentMethodId: true
};
export const useConfirmCardSetup = (options = defaultCardSetupOptions) => {
  const { usePaymentMethodId } = options;

  const userEmail = useSelector(userEmailSelector);
  const paymentMethodId = useSelector(accountPaymentMethodSelector);
  const accountPlan = useSelector(accountPlanSelector);
  const stripe = useStripe();
  const elements = useElements();

  const confirmCardSetup = useCallback(
    async ({ paymentIntent, onResolve = () => null }) => {
      let result;
      if (usePaymentMethodId && paymentMethodId) {
        result = await stripe.confirmCardSetup(paymentIntent.payload.clientSecret, {
          payment_method: paymentMethodId
        });
      } else {
        result = await stripe.confirmCardSetup(paymentIntent.payload.clientSecret, {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: userEmail
            }
          }
        });
      }

      if (!isAppInWhiteLabelMode() && result.error) {
        switch (result.error.code) {
          case confirmCardSetupErrorCodes.SETUP_INTENT_AUTHENTICATION_FAILURE:
            result.error.message = i18n.t("accountCardDeclined.setupIntentAuthenticationFailure");
            break;
        }
      }
      onResolve(result);
    },
    [stripe, elements, paymentMethodId, accountPlan, userEmail, usePaymentMethodId]
  );

  return confirmCardSetup;
};

const linkedDebtDefaultValues = [null, null, null, null];
export const getLinkedDebtsRelatedValues = (state, portfolio, relatedId, currency, currencyCellValue) => {
  if (relatedId) {
    const debtLinked = filterDataFromPortfolioDetails(
      portfolio.details,
      custodian => relatedId.includes(custodian.id),
      void 0,
      sheet => sheet.category === category.DEBT
    );

    if (debtLinked.rows.length === 0) {
      return linkedDebtDefaultValues;
    }

    const portfolioValueTickerId = getTickerUsingShortName(state, portfolio.currency).id;
    const debtLinkedRows = debtLinked.rows.length
      ? debtLinked.rows
      : [
          {
            value: 0,
            valueTickerId: portfolioValueTickerId
          }
        ];

    const cellExchangeRate = getExchangeRate(state, currency, portfolio.currency);

    const debtExchangeRates = debtLinkedRows.map(debtLinkedRow =>
      getExchangeRate(
        state,
        getTickerUsingId(state, debtLinkedRow.valueTickerId || portfolioValueTickerId).shortName,
        portfolio.currency
      )
    );

    const debtValueToMinus = debtLinkedRows.reduce((acc, debtLinkedRow, index) => {
      return acc + calcCustodianOwnershipValue(debtLinkedRow.value * debtExchangeRates[index], debtLinkedRow.ownership);
    }, 0);
    //equity is already factored into the cell value
    const diff = (cellExchangeRate ? currencyCellValue * cellExchangeRate : currencyCellValue) - debtValueToMinus;

    return [
      shortFormatNumberWithCurrency(state, diff, portfolio.currency, true, true),
      getHomeLTV(debtValueToMinus, cellExchangeRate ? currencyCellValue * cellExchangeRate : currencyCellValue),
      debtValueToMinus,
      debtLinkedRows
    ];
  }

  return linkedDebtDefaultValues;
};
